import { useCallback, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { CONFIG } from "../config";
import * as utils from "../utils";

export const useExport = (filteredSessions, hourlyPeakData) => {
  const createSummaryData = useCallback(() => {
    const totalSessions = filteredSessions.length;
    const activeSessions = filteredSessions.filter((s) => s.isActive).length;
    const completedSessions = filteredSessions.filter(
      (s) => !s.isActive
    ).length;
    const uniqueUsers = new Set(filteredSessions.map((s) => s.userName)).size;
    const peakHour = utils.formatPeakHour(hourlyPeakData);
    const purposeBreakdown = {};
    filteredSessions.forEach((session) => {
      const purpose = session.purposeOfVisit || "Not specified";
      purposeBreakdown[purpose] = (purposeBreakdown[purpose] || 0) + 1;
    });
    // Count unique users by user type
    const uniqueUsersByType = {};
    const uniqueUsersByProgram = {};
    const departmentBreakdown = {};
    const uniqueUserSessions = [];
    const seenUsers = new Set();
    filteredSessions.forEach((session) => {
      if (!seenUsers.has(session.userName)) {
        seenUsers.add(session.userName);
        uniqueUserSessions.push(session);
      }
    });
    uniqueUserSessions.forEach((session) => {
      const userProfile = session.userProfile;
      if (
        userProfile &&
        userProfile.user_types &&
        userProfile.user_types.length > 0
      ) {
        const userType = userProfile.user_types[0].user_type; // Get first user type
        const program = session.program || "No Program";
        const department = userProfile.department || "No Department";
        uniqueUsersByType[userType] = (uniqueUsersByType[userType] || 0) + 1;
        // Count by program for each user type
        const programKey = `${userType} - ${program}`;
        uniqueUsersByProgram[programKey] =
          (uniqueUsersByProgram[programKey] || 0) + 1;
        // Department breakdown structure
        if (!departmentBreakdown[department]) {
          departmentBreakdown[department] = {};
        }
        if (!departmentBreakdown[department][program]) {
          departmentBreakdown[department][program] = {
            Faculty: 0,
            Student: 0,
          };
        }
        departmentBreakdown[department][program][userType] =
          (departmentBreakdown[department][program][userType] || 0) + 1;
      }
    });
    return {
      totalSessions,
      activeSessions,
      completedSessions,
      uniqueUsers,
      peakHour,
      purposeBreakdown,
      uniqueUsersByType,
      uniqueUsersByProgram,
      departmentBreakdown,
    };
  }, [filteredSessions, hourlyPeakData]);

  const exportHandlers = useMemo(
    () => ({
      [CONFIG.EXPORT_TYPES.PDF]: () => {
        const doc = new jsPDF();
        const summaryData = createSummaryData();
        const tableData = filteredSessions.map(utils.formatSessionForExport);

        // Helper function to center text
        const centerText = (doc, text, y) => {
          const pageWidth = doc.internal.pageSize.getWidth();
          const textWidth =
            (doc.getStringUnitWidth(text) * doc.internal.getFontSize()) /
            doc.internal.scaleFactor;
          const x = (pageWidth - textWidth) / 2;
          doc.text(text, x, y);
        };

        // Header
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        centerText(doc, "Complete Session History Report", 16);
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        centerText(doc, `Generated on: ${new Date().toLocaleString()}`, 22);

        // Summary Statistics
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Summary Statistics:", 14, 30);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        const summaryLines = [
          `• Total Sessions: ${summaryData.totalSessions}`,
          `• Unique Users: ${summaryData.uniqueUsers}`,
          `• Active Sessions: ${summaryData.activeSessions}`,
          `• Completed Sessions: ${summaryData.completedSessions}`,
          `• Peak Hour: ${summaryData.peakHour}`,
        ];
        let contentY = 36;
        summaryLines.forEach((line) => {
          doc.text(line, 14, contentY);
          contentY += 5;
        });

        // Purpose of Visit Breakdown
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("Purpose of Visit Breakdown:", 90, 30);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        let purposeY = 36;
        Object.entries(summaryData.purposeBreakdown).forEach(
          ([purpose, count]) => {
            doc.text(`- ${purpose}: ${count}`, 90, purposeY);
            purposeY += 5;
          }
        );

        // User Type Breakdown
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.text("User Type Breakdown:", 160, 30);
        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        let userTypeY = 36;
        Object.entries(summaryData.uniqueUsersByType).forEach(
          ([userType, count]) => {
            doc.text(`- ${userType}: ${count} unique users`, 160, userTypeY);
            userTypeY += 5;
          }
        );

        // Department Breakdown Table
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Department Breakdown:", 14, userTypeY + 20); // Increased the gap here

        // Prepare data for the department breakdown table
        const deptTableData = [];
        Object.entries(summaryData.departmentBreakdown).forEach(
          ([dept, programs]) => {
            Object.entries(programs).forEach(([program, userTypes]) => {
              deptTableData.push([
                dept,
                program,
                userTypes.Faculty || 0,
                userTypes.Student || 0,
              ]);
            });
          }
        );

        // Add the department breakdown table
        autoTable(doc, {
          head: [["Department", "Program", "Faculty", "Student"]],
          body: deptTableData,
          startY: userTypeY + 25, // Also increased the startY of the table for additional gap
          margin: { left: 10, right: 10 },
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [52, 73, 94], fontSize: 10, textColor: 255 },
          tableWidth: "auto", // Use the full width of the page
        });

        // Main sessions table
        autoTable(doc, {
          head: [CONFIG.TABLE_HEADERS],
          body: tableData,
          startY: doc.lastAutoTable.finalY + 10,
          margin: { left: 10, right: 10 },
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [52, 73, 94], fontSize: 10, textColor: 255 },
          tableWidth: "auto", // Use the full width of the page
        });

        doc.save("complete-session-history.pdf");
      },
      [CONFIG.EXPORT_TYPES.EXCEL]: () => {
        const summaryData = createSummaryData();
        const wsData = [
          ["Complete Session History Report"],
          [`Generated on: ${new Date().toLocaleString()}`],
          [],
          ["Summary Statistics:"],
          ["Total Sessions:", summaryData.totalSessions],
          ["Unique Users:", summaryData.uniqueUsers],
          ["Active Sessions:", summaryData.activeSessions],
          ["Completed Sessions:", summaryData.completedSessions],
          ["Peak Hour:", summaryData.peakHour],
          [],
          ["Purpose of Visit Breakdown:"],
          ...Object.entries(summaryData.purposeBreakdown).map(
            ([purpose, count]) => [purpose, count]
          ),
          [],
          ["User Type Breakdown:"],
          ...Object.entries(summaryData.uniqueUsersByType).map(
            ([userType, count]) => [userType, `${count} unique users`]
          ),
          [],
          ["Department → Program → Faculty/Student Breakdown:"],
          ...Object.entries(summaryData.departmentBreakdown).flatMap(
            ([dept, programs]) => [
              [dept],
              ...Object.entries(programs).flatMap(([program, userTypes]) => [
                [`   - ${program}`],
                [`      Faculty: ${userTypes.Faculty || 0}`],
                [`      Student: ${userTypes.Student || 0}`],
              ]),
            ]
          ),
          [],
          CONFIG.TABLE_HEADERS,
          ...filteredSessions.map(utils.formatSessionForExport),
        ];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws["!cols"] = [
          { wch: 25 },
          { wch: 15 },
          { wch: 15 },
          { wch: 10 },
          { wch: 20 },
          { wch: 20 },
          { wch: 20 },
          { wch: 15 },
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Complete Sessions");
        XLSX.writeFile(wb, "complete-session-history.xlsx");
      },
      [CONFIG.EXPORT_TYPES.CSV]: () => {
        const summaryData = createSummaryData();
        const csvData = [
          ["Complete Session History Report"],
          [`Generated on: ${new Date().toLocaleString()}`],
          [],
          ["Summary Statistics:"],
          [`Total Sessions,${summaryData.totalSessions}`],
          [`Unique Users,${summaryData.uniqueUsers}`],
          [`Active Sessions,${summaryData.activeSessions}`],
          [`Completed Sessions,${summaryData.completedSessions}`],
          [`Peak Hour,${summaryData.peakHour}`],
          [],
          ["Purpose of Visit Breakdown:"],
          ...Object.entries(summaryData.purposeBreakdown).map(
            ([purpose, count]) => [purpose, count]
          ),
          [],
          ["User Type Breakdown:"],
          ...Object.entries(summaryData.uniqueUsersByType).map(
            ([userType, count]) => [`${userType}`, `${count} unique users`]
          ),
          [],
          ["Program Breakdown by User Type:"],
          ...Object.entries(summaryData.uniqueUsersByProgram).map(
            ([programKey, count]) => [programKey, count]
          ),
          [],
          CONFIG.TABLE_HEADERS,
          ...filteredSessions.map(utils.formatSessionForExport),
        ];
        const csvContent = csvData
          .map((row) => row.map((field) => `"${field}"`).join(","))
          .join("\n");
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "complete-session-history.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
    }),
    [filteredSessions, createSummaryData]
  );

  return { exportHandlers, createSummaryData };
};
